import type { StorefrontProduct, StorefrontVariant } from "./types";

type ScheduledPricing = Pick<
  StorefrontVariant,
  "price" | "regularPrice" | "salePrice" | "scheduledSalePrice" | "saleStartsAt" | "saleEndsAt" | "onSale"
>;

function parsedBoundary(value: string | undefined): number | undefined | null {
  if (!value) return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

/**
 * Re-evaluates a scheduled price in the browser. Public catalog responses can
 * remain open across a sale boundary, so their server-computed `onSale` flag is
 * intentionally not treated as authoritative when a sale amount is present.
 */
export function resolveScheduledStorefrontPricing<T extends ScheduledPricing>(
  value: T,
  now = Date.now(),
): T {
  const scheduledSalePrice = value.scheduledSalePrice ?? value.salePrice;
  if (scheduledSalePrice === undefined) return value;

  const startsAt = parsedBoundary(value.saleStartsAt);
  const endsAt = parsedBoundary(value.saleEndsAt);
  const validWindow = startsAt !== null && endsAt !== null;
  const withinWindow =
    validWindow &&
    (startsAt === undefined || startsAt <= now) &&
    (endsAt === undefined || endsAt > now);
  const isDiscount = value.regularPrice === undefined || scheduledSalePrice < value.regularPrice;
  const onSale = withinWindow && isDiscount;

  return {
    ...value,
    price: onSale ? scheduledSalePrice : value.regularPrice ?? value.price,
    salePrice: onSale ? scheduledSalePrice : undefined,
    scheduledSalePrice,
    onSale,
  };
}

function resolveRelatedProductPricing(product: StorefrontProduct, now: number): StorefrontProduct {
  const variants = product.variants.map((variant) => resolveScheduledStorefrontPricing(variant, now));
  const resolvedProduct = resolveScheduledStorefrontPricing(product, now);
  const defaultVariant =
    variants.find((variant) => variant.id === product.variantId) ??
    variants.find((variant) => variant.inStock) ??
    variants[0];

  return {
    ...resolvedProduct,
    ...(defaultVariant
      ? {
          variantId: defaultVariant.id,
          price: defaultVariant.price,
          regularPrice: defaultVariant.regularPrice,
          salePrice: defaultVariant.salePrice,
          scheduledSalePrice: defaultVariant.scheduledSalePrice,
          saleStartsAt: defaultVariant.saleStartsAt,
          saleEndsAt: defaultVariant.saleEndsAt,
          onSale: defaultVariant.onSale,
          taxPrices: defaultVariant.taxPrices,
        }
      : {}),
    variants,
  };
}

/** Effective current price range for cards representing variable products. */
export function storefrontProductPriceRange(
  product: StorefrontProduct,
): { minimum: number; maximum: number } | null {
  const prices = product.variants
    .map((variant) => variant.price)
    .filter((price): price is number => typeof price === "number" && Number.isFinite(price));
  if (prices.length === 0) {
    return typeof product.price === "number" && Number.isFinite(product.price)
      ? { minimum: product.price, maximum: product.price }
      : null;
  }
  return { minimum: Math.min(...prices), maximum: Math.max(...prices) };
}

/** Returns a product with live prices, including its one-level related items. */
export function resolveStorefrontProductPricing(
  product: StorefrontProduct,
  now = Date.now(),
): StorefrontProduct {
  const resolved = resolveRelatedProductPricing(product, now);
  return {
    ...resolved,
    upsells: product.upsells.map((item) => resolveRelatedProductPricing(item, now)),
    crossSells: product.crossSells.map((item) => resolveRelatedProductPricing(item, now)),
  };
}

/** Finds the first future start/end instant that should trigger a price render. */
export function nextStorefrontPriceBoundary(
  products: readonly StorefrontProduct[],
  after = Date.now(),
): number | undefined {
  let next: number | undefined;
  const visited = new Set<StorefrontProduct>();

  const consider = (value: string | undefined) => {
    const timestamp = parsedBoundary(value);
    if (timestamp === undefined || timestamp === null || timestamp <= after) return;
    next = next === undefined ? timestamp : Math.min(next, timestamp);
  };
  const visit = (product: StorefrontProduct) => {
    if (visited.has(product)) return;
    visited.add(product);
    consider(product.saleStartsAt);
    consider(product.saleEndsAt);
    for (const variant of product.variants) {
      consider(variant.saleStartsAt);
      consider(variant.saleEndsAt);
    }
    for (const related of product.upsells) visit(related);
    for (const related of product.crossSells) visit(related);
  };

  for (const product of products) visit(product);
  return next;
}
